import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Eye, Loader2, Send, X, Mail, Users } from 'lucide-react';
import { useCommunity } from '@/contexts/CommunityContext';
import { useCampaignEmailPreview } from '@/hooks/useCampaignEmailPreview';
import { type TemplateType } from '@/utils/emailPreviewUtils';

interface TargetedUser {
  user_id: string;
  business_id?: string;
  user_email: string;
  user_name: string;
  business_name?: string;
  created_at: string;
}

interface CampaignRule {
  id: string;
  name: string;
  description: string;
}

interface CampaignEmailPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignRule: CampaignRule;
  targetedUsers: TargetedUser[];
  selectedRecipients: string[];
  templateType: TemplateType;
  onConfirmSend: () => void;
}

const CampaignEmailPreviewModal = ({
  open,
  onOpenChange,
  campaignRule,
  targetedUsers,
  selectedRecipients,
  templateType,
  onConfirmSend
}: CampaignEmailPreviewModalProps) => {
  const { currentCommunity } = useCommunity();
  const [selectedPreviewUserId, setSelectedPreviewUserId] = useState<string>('');

  const {
    emailTemplate,
    templateLoading,
    previewRecipients,
    selectedRecipient,
    processedEmail,
    isReady
  } = useCampaignEmailPreview({
    templateType,
    targetedUsers,
    selectedRecipients,
    selectedPreviewUserId
  });

  // Reset and auto-select first recipient when modal opens or recipients change
  useEffect(() => {
    if (open && previewRecipients.length > 0) {
      setSelectedPreviewUserId(previewRecipients[0].user_id);
    } else if (!open) {
      // Reset selection when modal closes
      setSelectedPreviewUserId('');
    }
  }, [open, previewRecipients]);

  // Additional effect to handle when previewRecipients change but modal is already open
  useEffect(() => {
    if (previewRecipients.length > 0 && (!selectedPreviewUserId || !previewRecipients.find(r => r.user_id === selectedPreviewUserId))) {
      setSelectedPreviewUserId(previewRecipients[0].user_id);
    }
  }, [previewRecipients, selectedPreviewUserId]);

  const isLoading = templateLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[100vw] sm:w-[90vw] max-w-none sm:max-w-4xl h-[100vh] sm:h-auto sm:max-h-[90vh] p-3 sm:p-6 m-0 sm:m-4 rounded-none sm:rounded-lg flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg leading-tight">
            <Eye className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
            <span className="break-words">Campaign Email Preview</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Loading email preview...</span>
            </div>
          ) : !emailTemplate ? (
            <div className="text-center py-8">
              <Mail className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-muted-foreground">No email template found for this campaign type.</p>
              <p className="text-sm text-muted-foreground mt-1">
                Please create an email template for "{templateType}" first.
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-6 pb-4">
              {/* Campaign Info */}
              <div className="p-3 sm:p-4 bg-muted rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                  <h3 className="font-semibold flex items-center gap-2 min-w-0">
                    <Users className="h-4 w-4 flex-shrink-0" />
                    <span className="break-words">{campaignRule.name}</span>
                  </h3>
                  <Badge variant="secondary" className="self-start sm:self-auto">
                    {selectedRecipients.length} recipient{selectedRecipients.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground break-words">{campaignRule.description}</p>
              </div>

              {/* Preview As Controls */}
              <div className="p-3 sm:p-4 bg-muted rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                  <Label htmlFor="preview-recipient" className="text-sm font-medium whitespace-nowrap">
                    Preview as:
                  </Label>
                  <Select value={selectedPreviewUserId} onValueChange={setSelectedPreviewUserId}>
                    <SelectTrigger id="preview-recipient" className="w-full sm:max-w-sm">
                      <SelectValue placeholder="Select recipient to preview">
                        {selectedRecipient && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{selectedRecipient.user_name}</span>
                            {selectedRecipient.business_name && (
                              <span className="text-muted-foreground">• {selectedRecipient.business_name}</span>
                            )}
                          </div>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="z-50" sideOffset={5}>
                      {previewRecipients.map(recipient => (
                        <SelectItem key={recipient.user_id} value={recipient.user_id}>
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{recipient.user_name}</span>
                              {recipient.business_name && (
                                <span className="text-muted-foreground">• {recipient.business_name}</span>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">{recipient.user_email}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Email Preview */}
              {isReady && (
                <div className="space-y-4">
                  <div className="p-3 sm:p-4 bg-muted rounded-lg">
                    <h4 className="font-medium mb-2 text-sm sm:text-base">Subject:</h4>
                    <p className="text-sm break-words">{processedEmail.subject}</p>
                  </div>

                  <div className="p-3 sm:p-4 bg-muted rounded-lg">
                    <h4 className="font-medium mb-2 text-sm sm:text-base">From:</h4>
                    <p className="text-sm break-all">
                      {emailTemplate.sender_name} &lt;{emailTemplate.local_part}@{currentCommunity?.email_domain || 'community.com'}&gt;
                    </p>
                  </div>

                  <Tabs defaultValue="html" className="w-full">
                    <TabsList className="grid grid-cols-2 w-full sm:w-auto sm:flex">
                      <TabsTrigger value="html" className="text-xs sm:text-sm">HTML Preview</TabsTrigger>
                      <TabsTrigger value="text" className="text-xs sm:text-sm">Text Version</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="html" className="mt-2 sm:mt-4">
                      <div className="border rounded-lg overflow-hidden">
                        <div 
                          className="w-full min-h-[200px] sm:min-h-[400px] bg-white p-1 sm:p-4 overflow-x-auto text-xs sm:text-sm"
                          dangerouslySetInnerHTML={{ __html: processedEmail.html }}
                        />
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="text" className="mt-2 sm:mt-4">
                      <div className="border rounded-lg p-2 sm:p-4 bg-white overflow-x-auto">
                        <pre className="whitespace-pre-wrap text-xs sm:text-sm font-mono break-words">
                          {processedEmail.text || 'No text version available'}
                        </pre>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 flex-shrink-0 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="gap-2 w-full sm:w-auto order-2 sm:order-1">
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button 
            onClick={onConfirmSend}
            disabled={!isReady}
            className="gap-2 w-full sm:w-auto order-1 sm:order-2"
          >
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">Send to {selectedRecipients.length} recipient{selectedRecipients.length !== 1 ? 's' : ''}</span>
            <span className="sm:hidden">Send ({selectedRecipients.length})</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignEmailPreviewModal;